// One row in the "Requests need action" section (#1600). Renders ONLY the
// request public code (`id`, format `LR-#####`) + a badge per derived
// signal. NO PII: name, route, addresses, insurance, chat, items, and
// carrier detail are never passed in — the row props carry the non-PII
// `MyDayRequestSignalRow` projection only (AC-9). Clicking the row opens
// the request via the section-owned open mechanism.
//
// WHY a local row shell instead of a shared primitive: the bucket
// `MyDayTaskRow` is bound to the lead-task `MyDayItem` shape (lead code,
// due chip, complete button); a request-signal row carries a different
// payload (request code + signal badges + open-on-click), so it does not
// fit that component's contract.
import { useTranslation } from 'react-i18next';
import type { MyDayRequestSignalKind, MyDayRequestSignalRow as Row } from '../requestSignals.js';

export interface MyDayRequestSignalRowProps {
  row: Row;
  onOpen: (id: string) => void;
}

const SIGNAL_LABEL_KEY: Record<
  MyDayRequestSignalKind,
  | 'requests.signal.returned'
  | 'requests.signal.slaHot'
  | 'requests.signal.readyToPick'
  | 'requests.signal.unreadChat'
> = {
  returned: 'requests.signal.returned',
  slaHot: 'requests.signal.slaHot',
  readyToPick: 'requests.signal.readyToPick',
  unreadChat: 'requests.signal.unreadChat',
};

// Error/neutral tones reuse the same status-token palette the lead-task
// rows use, so the section reads in the shared visual language.
const SIGNAL_TONE: Record<MyDayRequestSignalKind, { bg: string; fg: string }> = {
  returned: { bg: 'var(--status-error-bg)', fg: 'var(--status-error-fg)' },
  slaHot: { bg: 'var(--status-error-bg)', fg: 'var(--status-error-fg)' },
  readyToPick: { bg: 'var(--status-info-bg)', fg: 'var(--status-info-fg)' },
  unreadChat: { bg: 'var(--status-neutral-bg)', fg: 'var(--status-neutral-fg)' },
};

export function MyDayRequestSignalRow({ row, onOpen }: MyDayRequestSignalRowProps) {
  const { t } = useTranslation('myDay');
  return (
    <button
      type="button"
      data-testid={`my-day-request-row-${row.id}`}
      data-primary-signal={row.primary}
      onClick={() => onOpen(row.id)}
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        width: '100%',
        textAlign: 'left',
        padding: '8px 10px',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-1)',
        background: 'var(--bg-0)',
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
      }}
    >
      <span
        data-testid={`my-day-request-row-${row.id}-code`}
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--fg-2)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {row.id}
      </span>
      <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
        {row.signals.map((kind) => (
          <span
            key={kind}
            data-testid={`my-day-request-row-${row.id}-badge-${kind}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '2px 8px',
              borderRadius: 'var(--r-full)',
              fontSize: 11,
              fontWeight: 600,
              background: SIGNAL_TONE[kind].bg,
              color: SIGNAL_TONE[kind].fg,
            }}
          >
            {t(SIGNAL_LABEL_KEY[kind])}
          </span>
        ))}
      </span>
    </button>
  );
}
